import { Link } from "react-router-dom";
import { Instagram, Youtube } from "lucide-react";
import { BRANCHES } from "@/lib/data";

const LOGO_URL = "/brand/phoenix-logo.png";

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-black border-t border-white/10 mt-20">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Phoenix" className="h-14 w-auto object-contain" />
              <span
                className="font-display text-lg tracking-wider text-white"
                style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.08em" }}
              >
                PHOENIX DANCE ACADEMY
              </span>
            </Link>
            <p className="mt-6 text-sm text-white/60 leading-relaxed max-w-sm">
              An international dance academy training the next generation of performers across India, USA, and Canada.
            </p>
            <div className="mt-8 flex gap-3">
              <a
                href="https://instagram.com/phoenix_dance_academy_pda"
                target="_blank"
                rel="noreferrer"
                aria-label="Instagram"
                data-testid="footer-social-instagram"
                className="w-10 h-10 flex items-center justify-center border border-white/15 text-white/60 hover:border-[#ff4500] hover:text-[#ff4500] transition-colors"
              >
                <Instagram className="w-4 h-4" strokeWidth={1.5} />
              </a>
              <a
                href="https://youtube.com/@phoenixdanceacademy6789"
                target="_blank"
                rel="noreferrer"
                aria-label="YouTube"
                data-testid="footer-social-youtube"
                className="w-10 h-10 flex items-center justify-center border border-white/15 text-white/60 hover:border-[#ff4500] hover:text-[#ff4500] transition-colors"
              >
                <Youtube className="w-4 h-4" strokeWidth={1.5} />
              </a>
            </div>
          </div>

          <div className="lg:col-span-2">
            <p className="text-xs uppercase tracking-[0.2em] text-[#ffb700] mb-5">Explore</p>
            <ul className="space-y-3 text-sm text-white/70">
              <li><Link to="/about" className="hover:text-[#ff4500]">About</Link></li>
              <li><Link to="/classes" className="hover:text-[#ff4500]">Classes</Link></li>
              <li><Link to="/services" className="hover:text-[#ff4500]">Services</Link></li>
              <li><Link to="/instructors" className="hover:text-[#ff4500]">Founders & Our Team</Link></li>
              <li><Link to="/awards" className="hover:text-[#ff4500]">Awards</Link></li>
              <li><Link to="/gallery" className="hover:text-[#ff4500]">Gallery</Link></li>
            </ul>
          </div>
          <div className="lg:col-span-2">
            <p className="text-xs uppercase tracking-[0.2em] text-[#ffb700] mb-5">Programs</p>
            <ul className="space-y-3 text-sm text-white/70">
              <li><Link to="/classes" className="hover:text-[#ff4500]">Kids Dance</Link></li>
              <li>
                <Link
                  to="/classes"
                  data-testid="footer-program-beginners"
                  onClick={() => window.scrollTo({ top: 0, behavior: "instant" })}
                  className="hover:text-[#ff4500]"
                >
                  Beginners
                </Link>
              </li>
              <li>
                <Link
                  to="/classes"
                  data-testid="footer-program-advanced"
                  onClick={() => window.scrollTo({ top: 0, behavior: "instant" })}
                  className="hover:text-[#ff4500]"
                >
                  Advanced
                </Link>
              </li>
              <li><Link to="/services" className="hover:text-[#ff4500]">Wedding Choreo</Link></li>
              <li><Link to="/book-trial" className="hover:text-[#ff4500]">Book Free Trial</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-4">
            <p className="text-xs uppercase tracking-[0.2em] text-[#ffb700] mb-5">Branches</p>
            <ul className="space-y-4 text-sm text-white/70">
              {BRANCHES.map((b) => (
                <li key={b.id} className="flex gap-3">
                  <span className="text-base">{b.flag}</span>
                  <div>
                    <p className="text-white font-medium">{b.city}, {b.name}</p>
                    {b.locations.map((loc) => (
                      <div key={loc.id} className="mt-2">
                        <p className="text-xs text-white/50">
                          <span className="text-white/70">{loc.name}:</span> {loc.address}
                        </p>
                        {loc.phone && (
                          <a
                            href={`tel:${loc.phone.replace(/\s/g, "")}`}
                            className="text-xs text-white/60 hover:text-[#ff4500] transition-colors"
                          >
                            {loc.phone}
                          </a>
                        )}
                        {b.altPhone && (
                          <>
                            <span className="text-xs text-white/30 px-1">·</span>
                            <a
                              href={`tel:${b.altPhone.replace(/\s/g, "")}`}
                              data-testid={`footer-altphone-${b.id}`}
                              className="text-xs text-white/60 hover:text-[#ff4500] transition-colors"
                            >
                              {b.altPhone}
                            </a>
                          </>
                        )}
                      </div>
                    ))}
                    {!b.locations.some((l) => l.phone) && b.phone && (
                      <p className="text-xs text-white/50 mt-1">{b.phone}</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-white/40">
          <p>© {new Date().getFullYear()} Phoenix Dance Academy. All rights reserved.</p>
          <div className="flex gap-6">
            <Link to="/privacy" data-testid="footer-privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" data-testid="footer-terms" className="hover:text-white transition-colors">Terms</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
